import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, TrendingUp, Users, Clock } from 'lucide-react';

interface PrayerSuggestion {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
}

interface PrayerStreak {
  currentStreak: number;
  longestStreak: number;
  totalPrayers: number;
}

interface PrayerSuggestionsProps {
  suggestions: PrayerSuggestion[];
  streak: PrayerStreak | null;
  communityRequests: any[];
  onSuggestionClick: (suggestion: PrayerSuggestion) => void;
  onCommunityRequestClick: (request: any) => void;
  isLoading?: boolean;
}

export default function PrayerSuggestions({
  suggestions,
  streak,
  communityRequests,
  onSuggestionClick,
  onCommunityRequestClick,
  isLoading = false
}: PrayerSuggestionsProps) {
  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {/* Skeleton for streak */}
        <div className="bg-gradient-to-r from-gold/10 to-gold/5 rounded-lg p-4">
          <div className="h-4 bg-gold/20 rounded w-1/3 mb-2"></div>
          <div className="h-6 bg-gold/30 rounded w-1/2"></div>
        </div>
        
        {/* Skeleton for suggestions */}
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white/50 rounded-lg p-3 h-16"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Prayer Streak Display */}
      {streak && (
        <div className="bg-gradient-to-r from-gold/10 to-gold/5 border border-gold/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-gold" />
            <span className="text-sm font-medium text-prayer-primary">Prayer Streak</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gold">{streak.currentStreak}</div>
              <div className="text-xs text-prayer-primary/70">
                {streak.currentStreak === 1 ? 'day' : 'days'} in a row
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-prayer-primary/80">
                Best: {streak.longestStreak} days
              </div>
              <div className="text-xs text-prayer-primary/60">
                Total: {streak.totalPrayers} prayers
              </div>
            </div>
          </div>
          
          {/* Encouragement message */}
          {streak.currentStreak > 0 && (
            <div className="mt-3 text-xs text-prayer-primary/70 italic">
              {streak.currentStreak >= 7 
                ? "Amazing dedication! Keep up the faithful practice 🙏" 
                : streak.currentStreak >= 3
                ? "Great momentum! You're building a beautiful habit ✨"
                : "Every prayer matters. Keep going! 💚"}
            </div>
          )}
        </div>
      )}

      {/* Smart Prayer Suggestions */}
      {suggestions.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-gold" />
            <span className="text-sm font-medium text-prayer-primary">Suggested for You</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {suggestions.slice(0, 4).map((suggestion) => (
              <Button
                key={suggestion.id}
                onClick={() => onSuggestionClick(suggestion)}
                variant="outline"
                className="h-auto p-3 flex flex-col items-start gap-1 text-left border-gold/20 hover:border-gold/40 hover:bg-gold/5 transition-all duration-200 group"
              >
                <div className="flex items-center gap-2 w-full">
                  <span className="text-base group-hover:scale-110 transition-transform duration-200">
                    {suggestion.icon}
                  </span>
                  <span className="text-sm font-medium text-prayer-primary truncate">
                    {suggestion.title}
                  </span>
                </div>
                <span className="text-xs text-prayer-primary/60 line-clamp-1">
                  {suggestion.description}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Community Prayer Requests */}
      {communityRequests.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-gold" />
            <span className="text-sm font-medium text-prayer-primary">Community Requests</span>
          </div>
          <div className="space-y-2">
            {communityRequests.slice(0, 3).map((request) => (
              <Button
                key={request.id}
                onClick={() => onCommunityRequestClick(request)}
                variant="ghost"
                className="w-full h-auto p-3 text-left justify-start hover:bg-gold/5 border border-transparent hover:border-gold/20 transition-all duration-200"
              >
                <div className="space-y-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-prayer-primary truncate">
                      {request.title}
                    </span>
                    <Badge variant="secondary" className="text-xs bg-gold/10 text-gold border-gold/20">
                      {request.author_name}
                    </Badge>
                  </div>
                  <p className="text-xs text-prayer-primary/60 line-clamp-1">
                    {request.content}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-prayer-primary/50">
                    <Clock className="h-3 w-3" />
                    <span>{new Date(request.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}